"use client"

import { io } from "socket.io-client"

// Sistema de Socket.IO para rota padrão usando variáveis de ambiente
let socket: any = null
let reconnectAttempts = 0
const maxReconnectAttempts = 3
const reconnectDelay = 2000
let isConnecting = false

export type SocketEventCallback = (data: any) => void

const eventListeners: Record<string, SocketEventCallback[]> = {}

// URLs de Socket.IO das variáveis de ambiente
const getSocketUrls = () => {
  const urls = []
  const normalize = (u: string) => (/^(https?|wss?):\/\//i.test(u) ? u : `https://${u}`)

  if (process.env.NEXT_PUBLIC_SOCKET_URL) {
    urls.push(normalize(process.env.NEXT_PUBLIC_SOCKET_URL))
  }

  if (process.env.NEXT_PUBLIC_SOCKET_URL_FALLBACK) {
    urls.push(normalize(process.env.NEXT_PUBLIC_SOCKET_URL_FALLBACK))
  }

  // URLs padrão como fallback
  if (urls.length === 0) {
    urls.push("https://api.syncmenu.ao") // Prioridade para dev local
    urls.push("https://api.syncmenu.ao")
    urls.push("wss://api.syncmenu.ao")
  }

  console.log("[Socket.IO] URLs configuradas:", urls)
  return urls
}



export const initializeSocket = async (): Promise<any> => {
  if (typeof window === "undefined") return null

  // Se já está conectando, não tentar novamente
  if (isConnecting) {
    console.log("[Socket.IO] ⏳ Já está conectando...")
    return socket
  }

  // Se já tem uma conexão ativa, retornar
  if (socket && socket.connected) {
    console.log("[Socket.IO] ✅ Usando conexão existente")
    return socket
  }

  // Se excedeu tentativas de reconexão, não tentar mais
  if (reconnectAttempts >= maxReconnectAttempts) {
    console.log("[Socket.IO] ❌ Máximo de tentativas de reconexão atingido")
    return null
  }

  isConnecting = true
  const SOCKET_URLS = getSocketUrls()

  try {
    const tryConnection = (urlIndex = 0): void => {
      if (urlIndex >= SOCKET_URLS.length) {
        console.log("[Socket.IO] ❌ Todas as URLs falharam. Continuando sem Socket.IO...")
        isConnecting = false
        return
      }

      const socketUrl = SOCKET_URLS[urlIndex]
      console.log(`[Socket.IO] 🔌 Tentando conectar a ${socketUrl}`)

      try {
        socket = io(socketUrl, {
          transports: ["websocket", "polling"],
          timeout: 10000,
          reconnection: true,
          reconnectionAttempts: 3,
          reconnectionDelay: 2000,
          forceNew: false,
        })

        socket.on("connect", () => {
          console.log(`[Socket.IO] ✅ Conectado com sucesso: ${socketUrl}`)
          console.log(`[Socket.IO] 📡 Socket ID: ${socket.id}`)
          reconnectAttempts = 0
          isConnecting = false

          // Enviar ping para manter conexão ativa
          const pingInterval = setInterval(() => {
            if (socket && socket.connected) {
              socket.emit("ping")
            } else {
              clearInterval(pingInterval)
            }
          }, 30000)
        })

        // Listener para mensagens gerais
        socket.on("message", (data: any) => {
          console.log("[Socket.IO] 📨 Mensagem recebida:", data)
          handleSocketMessage(data)
        })

        // Listeners específicos para atualizações de pedido solicitados pelo backend
        const handleExplicitUpdate = (eventName: string, data: any) => {
          console.log(`[Socket.IO] 🎯 Evento '${eventName}' recebido:`, data)

          // Se o evento for atualizarPedidoId, notificamos quem está ouvindo
          if (eventName === "atualizarPedidoId") {
            if (eventListeners["atualizarPedidoId"]) {
              eventListeners["atualizarPedidoId"].forEach((callback) => callback(data))
            }
          }

          // Notificamos também o listener genérico de atualização
          if (eventListeners["atualizarPedido"]) {
            eventListeners["atualizarPedido"].forEach((callback) => callback(data))
          }

          // Processamento interno
          handleSocketMessage(data)
        }

        socket.on("atualizarPedido", (data: any) => handleExplicitUpdate("atualizarPedido", data))
        socket.on("atualizarPedidoSecao", (data: any) => handleExplicitUpdate("atualizarPedidoSecao", data))
        socket.on("atualizarPedidoId", (data: any) => handleExplicitUpdate("atualizarPedidoId", data))

        // Listener universal para capturar todos os eventos (para depuração profunda)
        socket.onAny((eventName: string, ...args: any[]) => {
          console.log(`[Socket.IO] 🌐 Evento Universal [${eventName}]:`, args)

          // Processar eventos que podem conter dados de pedido se não capturado acima
          if (!["atualizarPedido", "atualizarPedidoSecao", "atualizarPedidoId"].includes(eventName)) {
            args.forEach((arg) => {
              if (arg && typeof arg === "object" && (arg._id || arg.id)) {
                console.log(`[Socket.IO] 📦 Possível atualização de pedido em evento genérico (${eventName}):`, arg)
                handleSocketMessage(arg)
              }
            })
          }
        })

        socket.on("disconnect", (reason: string) => {
          console.log(`[Socket.IO] ❌ Desconectado: ${socketUrl}`, reason)
          isConnecting = false

          // Se não foi um fechamento normal e ainda há URLs para tentar
          if (reason !== "io client disconnect" && urlIndex < SOCKET_URLS.length - 1) {
            setTimeout(() => {
              tryConnection(urlIndex + 1)
            }, 2000)
          }
        })

        socket.on("connect_error", (error: any) => {
          console.log(`[Socket.IO] ❌ Erro para ${socketUrl}:`, error)

          if (socket) {
            socket.disconnect()
          }

          setTimeout(() => {
            tryConnection(urlIndex + 1)
          }, 1000)
        })
      } catch (error) {
        console.error(`[Socket.IO] ❌ Erro ao criar conexão para ${socketUrl}:`, error)
        isConnecting = false
        setTimeout(() => {
          tryConnection(urlIndex + 1)
        }, 1000)
      }
    }

    tryConnection()
    return socket
  } catch (error) {
    console.error("[Socket.IO] ❌ Erro ao inicializar:", error)
    isConnecting = false
    return null
  }
}

// Função para processar mensagens do socket
const handleSocketMessage = (data: any) => {
  try {
    console.log("[Socket.IO] 🔍 Processando mensagem:", data)

    // Para o evento específico atualizarPedidoId
    if (data.type === "atualizarPedidoId" || data.event === "atualizarPedidoId") {
      console.log("[Socket.IO] ✅ Evento atualizarPedidoId processado:", data)

      if (eventListeners["atualizarPedidoId"]) {
        eventListeners["atualizarPedidoId"].forEach((callback) => callback(data.data || data.payload || data))
      }
      return
    }

    // Se o data tem _id, pode ser uma atualização de pedido
    if (data._id) {
      console.log("[Socket.IO] 📦 Dados com _id detectados, enviando para listeners:", data)

      if (eventListeners["atualizarPedidoId"]) {
        eventListeners["atualizarPedidoId"].forEach((callback) => callback(data))
      }
    }

    // Tratamento genérico para outros eventos
    const { type, payload, event } = data
    const eventType = type || event

    if (eventType && eventListeners[eventType]) {
      eventListeners[eventType].forEach((callback) => callback(payload || data))
    }
  } catch (error) {
    console.error("[Socket.IO] ❌ Erro ao processar mensagem:", error)
  }
}

export const getSocket = (): any => {
  return socket
}

export const disconnectSocket = (): void => {
  if (socket) {
    socket.disconnect()
    socket = null
  }
  reconnectAttempts = maxReconnectAttempts
}

export const addEventListener = (event: string, callback: SocketEventCallback): (() => void) => {
  if (!eventListeners[event]) {
    eventListeners[event] = []
  }
  eventListeners[event].push(callback)
  console.log(`[Socket.IO] 👂 Listener adicionado para evento '${event}'`)

  // Retornar função de cleanup
  return () => {
    removeEventListener(event, callback)
  }
}

export const removeEventListener = (event: string, callback: SocketEventCallback): void => {
  if (eventListeners[event]) {
    eventListeners[event] = eventListeners[event].filter((cb) => cb !== callback)
    console.log(`[Socket.IO] 🗑️ Listener removido para evento '${event}'`)
  }
}

// Função para se juntar a um canal específico (para pedidos específicos)
export const joinOrderChannel = (orderId: string, secaoId?: string, estabelecimentoId?: string): void => {
  if (socket && socket.connected) {
    console.log(`[Socket.IO] 📡 Entrando nos canais para o pedido ${orderId}${secaoId ? ` e seção ${secaoId}` : ""}`)

    const channels = [
      `atualizarPedido_${orderId}`
    ]

    console.log(`[Socket.IO] Entrando na sala principal do pedido: atualizarPedido_${orderId}`)

    if (secaoId && estabelecimentoId) {
      channels.push(`secao_${secaoId}_${estabelecimentoId}`)
      console.log(`[Socket.IO] Entrando na sala da seção: secao_${secaoId}_${estabelecimentoId}`)
    }

    channels.forEach((channel) => {
      socket.emit("join", channel)
      socket.emit("joinRoom", channel)
      socket.emit("subscribe", channel)
      console.log(`[Socket.IO] 📡 Inscrito no canal: ${channel}`)
    })
  } else {
    console.log(`[Socket.IO] ⚠️ Não foi possível entrar no canal. Socket não conectado.`)
  }
}

// Função para sair de um canal específico
export const leaveOrderChannel = (orderId: string, secaoId?: string, estabelecimentoId?: string): void => {
  if (socket && socket.connected) {
    console.log(`[Socket.IO] 📤 Saindo dos canais para o pedido ${orderId}${secaoId ? ` e seção ${secaoId}` : ""}`)

    const channels = [
      `atualizarPedido_${orderId}`
    ]

    if (secaoId && estabelecimentoId) {
      channels.push(`secao_${secaoId}_${estabelecimentoId}`)
    }

    channels.forEach((channel) => {
      socket.emit("leave", channel)
      socket.emit("leaveRoom", channel)
      socket.emit("unsubscribe", channel)
      console.log(`[Socket.IO] 📤 Removido do canal: ${channel}`)
    })
  }
}

// Função para verificar se Socket.IO está disponível
export const isSocketIOSupported = (): boolean => {
  return typeof window !== "undefined"
}

// Função para verificar status da conexão
export const getConnectionStatus = (): string => {
  if (!socket) return "disconnected"

  if (socket.connected) return "connected"
  if (socket.connecting) return "connecting"
  if (socket.disconnected) return "disconnected"

  return "unknown"
}


